<script setup lang="ts">
import {
  PopoverPositionEnum,
  TirPmContextMenuButton,
  TirPmIconView,
  TirPmScrollbar,
  TirPmTooltipElement,
  TirPmPopover,
} from 'tir-components';
import { ChevronRightIcon, WalletIcon } from 'tir-style-system/icons/outline';
import { onMounted, ref } from 'vue';
import { GetOrderHistoryListRequestModel, GetOrderHistoryListResponseItemModel } from './models';
import { AdapterService } from './adapter';
import OrderTaskHistoryTypeEnum from './enums/OrderTaskHistoryTypeEnum';
import { PageRequestModel } from '@/models';

/**Адаптер */
const adapter = new AdapterService();

/**Параметры */
const props = defineProps({
  /**id юзера */
  userId: { type: Number },
  /**ID задачи */
  taskId: { type: Number },
});

/**История очередности получена */
const isHistoryLoaded = ref(false);
/**История очередности */
const historyOrder = ref<GetOrderHistoryListResponseItemModel[]>([]);
/**Кол-во истории очередности */
const historyOrderCount = ref<number>();
/**Загрузка */
const isLoading = ref<boolean>();

/**После рендера компонента */
onMounted(async () => {
  await getHistoryOrder();
});

/**Получить дату */
const getDateCreated = (date: Date) => {
  return new Date(date).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
  });
};
/**Получить время */
const getTimeCreated = (_date: Date) => {
  const date = new Date(String(_date).replace(/z/gi, ''));
  const timeZone = date.getTimezoneOffset() / 60;
  date.setHours(date.getHours() - timeZone);

  return date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });
};
/**Возвращает текстовое описание действия на основании его типа */
const getActionDescription = (actionType: OrderTaskHistoryTypeEnum) => {
  switch (actionType) {
    case OrderTaskHistoryTypeEnum.Attach:
      return 'Помещен';
    case OrderTaskHistoryTypeEnum.Remove:
      return 'Исключен';
    case OrderTaskHistoryTypeEnum.MoveLower:
      return 'Перемещен выше';
    case OrderTaskHistoryTypeEnum.MoveUpper:
      return 'Перемещен ниже';
  }
};
/**Получить историю изменений */
const getHistoryOrder = async () => {
  if (isHistoryLoaded.value) return;
  isLoading.value = true;

  const response = await adapter.getOrderHistoryList(
    new GetOrderHistoryListRequestModel({
      PerformerId: props.userId,
      TaskId: props.taskId,
      Page: new PageRequestModel({
        Skip: 0,
        Take: 10,
      }),
    }),
  );
  if (response.IsSuccess) {
    historyOrder.value = response.Value.Items;
    isHistoryLoaded.value = true;
  }
  isLoading.value = false;
};
/**Получить следующую историю изменений */
const getNextHistoryOrder = async () => {
  if (historyOrderCount.value == historyOrder.value.length) return;

  const response = await adapter.getOrderHistoryList(
    new GetOrderHistoryListRequestModel({
      PerformerId: props.userId,
      TaskId: props.taskId,
      Page: new PageRequestModel({
        Skip: historyOrder.value.length,
        Take: 10,
      }),
    }),
  );
  if (response.IsSuccess) {
    historyOrder.value.push(...response.Value.Items);
    historyOrderCount.value = response.Value.Page.Count;
  }
};
/**Скролинг плашки с историей изменений */
const onScroll = async (event: Event) => {
  const scrollElement = event.target as HTMLElement;
  const isScrollBottom =
    scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight;

  if (isScrollBottom) await getNextHistoryOrder();
};
</script>

<template>
  <TirPmPopover
    isNoTeleport
    disableContentBlur
    width="32.5rem"
    class="task-history-order__menu-item--popover"
    :position="PopoverPositionEnum.Right"
  >
    <template #default>
      <TirPmContextMenuButton>
        <div class="task-history-order__menu-item">
          <WalletIcon class="task-history-order__icon" />
          <div>История очередности</div>
        </div>
        <template #right>
          <ChevronRightIcon class="task-history-order__icon" />
        </template>
      </TirPmContextMenuButton>
    </template>
    <template #content>
      <TirPmScrollbar
        v-if="historyOrder.length"
        showOnHover
        class="task-history-order__scrollbar"
        @onScroll="onScroll"
      >
        <div
          v-for="historyElement in historyOrder"
          :key="historyElement.TaskId"
          class="task-history-order__history-item"
        >
          <TirPmTooltipElement isNoTeleport>
            <template #default>
              <TirPmIconView
                isRounded
                :isBordered="false"
                :size="24"
                :imageLoadUrl="historyElement.Creator.ImageUrl"
                :colorCode="historyElement.Creator.AvatarColor"
              >
                <template #mainSlot>
                  {{ historyElement.Creator.FirstName[0] }}{{ historyElement.Creator.LastName[0] }}
                </template>
              </TirPmIconView>
            </template>
            <template #content>
              <div class="task-history-order__creator-info">
                {{ historyElement.Creator.FirstName }} {{ historyElement.Creator.LastName }}
              </div>
            </template>
          </TirPmTooltipElement>
          <div>
            {{ getDateCreated(historyElement.DateCreate) }}
            [{{ getTimeCreated(historyElement.DateCreate) }}]
            {{ getActionDescription(historyElement.Type) }}
          </div>
        </div>
      </TirPmScrollbar>
      <div v-else class="task-history-order__empty-list">
        <TirPmIconView :isBordered="false" :size="56">
          <template #mainSlot>
            <WalletIcon class="task-history-order__big-iron" />
          </template>
        </TirPmIconView>
        <div>В истории пусто</div>
      </div>
    </template>
  </TirPmPopover>
</template>

<style scoped lang="scss">
.task-history-order {
  &__empty-list {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    font-size: 1.625rem;
    font-weight: 500;
    padding: 1rem;
  }
  &__creator-info {
    display: flex;
    justify-content: center;
    overflow-wrap: break-word;
  }
  &__menu-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  &__scrollbar {
    max-height: 24rem;
  }
  &__menu-item--popover {
    width: 100% !important;
  }
  &__icon {
    width: 1.5rem;
    min-width: 1.5rem;
    height: 1.5rem;
  }
  &__big-iron {
    width: 2.625rem !important;
    height: 2.625rem !important;
    color: var(--neutral-70, #686c73);
  }
  &__history-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1rem;
    font-weight: 400;
    color: var(--text-primary, #272d37);
    padding: 0.75rem;
  }
}
</style>
